import {
  applyPaths,
  applyStep2Paths,
  applySuccessPaths,
  officePaths,
} from "routes";

import { languages } from "@/i18n/settings";
import {
  AllowedLanguages,
  AvailableTargetAudience,
  TranslatedPath,
} from "@/types/generic";

const EMPLOYER_HOMEPAGE_URL_MAP = {
  [AllowedLanguages.En]: "choose-your-language",
  [AllowedLanguages.Fr]: "employeur",
  [AllowedLanguages.Nl]: "werkgever",
};

export function slugify(str: string) {
  return String(str)
    .normalize("NFKD") // split accented characters into their base characters and diacritical marks
    .replace(/[\u0300-\u036f]/g, "") // remove all the accents, which happen to be all in the \u03xx UNICODE block.
    .trim() // trim leading or trailing whitespace
    .toLowerCase() // convert to lowercase
    .replace(/[^a-z0-9 -]/g, "") // remove non-alphanumeric characters
    .replace(/\s+/g, "-") // replace spaces with hyphens
    .replace(/-+/g, "-"); // remove consecutive hyphens
}

export function generateHomepageUrl(
  audience: AvailableTargetAudience,
  locale: AllowedLanguages,
) {
  if (audience === AvailableTargetAudience.EMPLOYEE) {
    return `/${locale}`;
  }

  if (audience === AvailableTargetAudience.EMPLOYER) {
    return `/${locale}/${EMPLOYER_HOMEPAGE_URL_MAP[locale]}`;
  }

  return `/${locale}`;
}

export function generateSlug(title: string, id: string) {
  return `${slugify(title)}-${id}`;
}

export function generateJobUrl(
  locale: AllowedLanguages,
  title: string,
  id: string,
) {
  return `/${locale}/jobs/${generateSlug(title, id)}`;
}

export function generateJobOverviewUrl(locale: AllowedLanguages) {
  return `/${locale}/jobs/`;
}

export function generateApplyToAJobUrl({
  jobId,
  locale,
  officeKey,
  type,
}: {
  jobId?: string | null | undefined;
  locale: AllowedLanguages;
  officeKey?: string | null | undefined;
  type?: string | null | undefined;
}) {
  const params = new URLSearchParams({
    ...(jobId ? { jobId } : {}),
    ...(officeKey ? { officeKey } : {}),
    ...(type ? { type } : {}),
  });
  const searchParams = params.toString();
  return `/${locale}/${applyPaths[locale]}${searchParams ? `?${searchParams}` : ""}`;
}

export function generateApplyToAJobStep2Url({
  applicationKey,
  candidateToken,
  jobId,
  locale,
}: {
  applicationKey: string;
  candidateToken: string;
  jobId: string | null | undefined;
  locale: AllowedLanguages;
}) {
  const params = new URLSearchParams({
    applicationKey: applicationKey,
    candidateToken: candidateToken,
    ...(jobId ? { jobId } : {}),
  });
  const searchParams = params.toString();
  return `/${locale}/${applyPaths[locale]}/${applyStep2Paths[locale]}${searchParams ? `?${searchParams}` : ""}`;
}

export function generateApplyToAJobSuccessUrl({
  applicationKey,
  candidateToken,
  jobId,
  locale,
}: {
  applicationKey: string;
  candidateToken: string;
  jobId?: string | null;
  locale: AllowedLanguages;
}) {
  const params = new URLSearchParams({
    applicationKey: applicationKey,
    candidateToken: candidateToken,
    ...(jobId ? { jobId } : {}),
  });
  const searchParams = params.toString();
  return `/${locale}/${applyPaths[locale]}/${applyStep2Paths[locale]}/${applySuccessPaths[locale]}${searchParams ? `?${searchParams}` : ""}`;
}

export function generateOfficeUrl(
  locale: AllowedLanguages,
  title: string,
  id: string,
) {
  return `/${locale}/${officePaths[locale]}/${generateSlug(title, id)}`;
}

export function getIdFromSlug(slug: string) {
  return slug.split("-").pop() ?? "";
}

interface PathsObj {
  en: string;
  fr: string;
  nl: string;
}

export function generateTranslatedPathsArray(
  paths: PathsObj | null,
  fixedPath?: string | null,
): TranslatedPath[] {
  if (!paths) {
    // Path after locale is the same for all languages
    return languages.map((lang) => {
      return {
        language: lang,
        url: `/${lang}/${fixedPath}`,
      };
    });
  }

  return languages.map((lang) => {
    return {
      language: lang,
      url: fixedPath
        ? `/${lang}/${fixedPath}/${paths[lang]}`
        : `/${lang}/${paths[lang]}`,
    };
  });
}
